import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBrowserTTS } from '@/hooks/useBrowserTTS';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';

interface VoiceAction {
  action: string;
  params: Record<string, unknown>;
}

export interface ParsedGuestRequest {
  guest_name?: string;
  email?: string;
  phone?: string;
  check_in?: string;
  check_out?: string;
  guests_count?: number;
  adults?: number;
  children?: number;
  room_preferences?: string;
  special_requests?: string;
  budget?: string;
  summary?: string;
}

export interface DraftReply {
  reply_text: string;
  tone?: string;
}

export interface PriceCalculation {
  category_id?: string;
  category_name: string;
  rate_plan_id?: string;
  rate_plan_name?: string;
  check_in: string;
  check_out: string;
  nights: number;
  adults: number;
  children_count: number;
  total_price: number;
  average_per_night: number;
  daily_prices?: Array<{ date: string; price: number }>;
  error?: string;
}

export interface WhatsAppQuote {
  phone: string;
  message: string;
  whatsapp_url: string;
}

export interface VoiceCommandResult {
  success: boolean;
  actions: VoiceAction[];
  speak: string;
  results?: {
    stats?: { arrivals: number; departures: number };
    guests?: Array<{ id: string; first_name: string; last_name: string; phone: string }>;
    available_rooms?: Array<{ id: string; room_number: string; room_type: string }>;
    finance?: { revenue: number; bookings_count: number };
    price_calculation?: PriceCalculation;
    whatsapp_quote?: WhatsAppQuote;
  };
  parsedRequest?: ParsedGuestRequest;
  draftReply?: DraftReply;
  error?: string;
}

// Page route mapping
const pageRoutes: Record<string, string> = {
  'dashboard': '/admin',
  'bookings': '/admin/bookings-list',
  'room-grid': '/admin/room-grid',
  'guests': '/admin/guests',
  'analytics': '/admin/analytics',
  'finance': '/admin/finance',
  'settings': '/admin/settings',
  'ai-assistant': '/admin/ai-assistant',
  'rooms': '/admin/rooms',
  'calendar': '/admin/calendar',
};

export const useVoiceCommands = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentHotel } = useOptimizedHotel();
  const hotelId = currentHotel?.id;
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<VoiceCommandResult | null>(null);
  const [parsedRequest, setParsedRequest] = useState<ParsedGuestRequest | null>(null);
  const [draftReply, setDraftReply] = useState<DraftReply | null>(null);
  const [priceCalculation, setPriceCalculation] = useState<PriceCalculation | null>(null);
  const [whatsappQuote, setWhatsappQuote] = useState<WhatsAppQuote | null>(null);

  // Load voice settings
  const getVoiceSettings = useCallback(() => {
    try {
      const saved = localStorage.getItem('voice_settings');
      return saved ? JSON.parse(saved) : { mode: 'browser_tts', language: 'ru-RU' };
    } catch {
      return { mode: 'browser_tts', language: 'ru-RU' };
    }
  }, []);

  const settings = getVoiceSettings();
  
  const browserTTS = useBrowserTTS({
    language: settings.language,
    rate: settings.speechRate || 1.0,
    voiceUri: settings.browserVoiceUri,
  });

  const elevenLabsTTS = useElevenLabsTTS({
    language: settings.language?.substring(0, 2) || 'ru',
    onError: (error) => {
      console.warn('[ElevenLabs TTS] Falling back to browser TTS:', error);
    },
  });

  // Execute actions returned from the AI
  const executeActions = useCallback((actions: VoiceAction[], results?: VoiceCommandResult['results']) => {
    for (const { action, params } of actions) {
      console.log(`🎯 Executing action: ${action}`, params);

      switch (action) {
        case 'navigate':
          const page = params.page as string;
          const route = pageRoutes[page];
          if (route) {
            navigate(route);
            toast({
              title: "🧭 Навигация",
              description: `Переход: ${page}`,
            });
          }
          break;

        case 'search_bookings':
          const query = params.query as string;
          if (query) {
            navigate(`/admin/bookings-list?search=${encodeURIComponent(query)}`);
          } else {
            navigate('/admin/bookings-list');
          }
          break;

        case 'create_booking_draft':
          const bookingParams = new URLSearchParams();
          if (params.guest_name) bookingParams.set('guest', params.guest_name as string);
          if (params.check_in) bookingParams.set('from', params.check_in as string);
          if (params.check_out) bookingParams.set('to', params.check_out as string);
          if (params.guests_count) bookingParams.set('guests', String(params.guests_count));
          navigate(`/admin/room-grid?${bookingParams.toString()}`);
          toast({
            title: "📅 Черновик бронирования",
            description: `${params.guest_name || 'Гость'}: ${params.check_in} — ${params.check_out}`,
          });
          break;

        case 'find_available_rooms':
          const roomParams = new URLSearchParams();
          if (params.check_in) roomParams.set('from', params.check_in as string);
          if (params.check_out) roomParams.set('to', params.check_out as string);
          navigate(`/admin/room-grid?${roomParams.toString()}`);
          break;

        case 'parse_guest_request':
          const parsed: ParsedGuestRequest = {
            guest_name: params.guest_name as string,
            email: params.email as string,
            phone: params.phone as string,
            check_in: params.check_in as string,
            check_out: params.check_out as string,
            guests_count: params.guests_count as number,
            adults: params.adults as number,
            children: params.children as number,
            room_preferences: params.room_preferences as string,
            special_requests: params.special_requests as string,
            budget: params.budget as string,
            summary: params.summary as string,
          };
          setParsedRequest(parsed);
          
          toast({
            title: "📧 Письмо гостя распознано",
            description: parsed.summary || 'Детали извлечены',
          });
          break;

        case 'draft_reply_to_guest':
          const reply: DraftReply = {
            reply_text: params.reply_text as string,
            tone: params.tone as string,
          };
          setDraftReply(reply);
          
          toast({
            title: "✉️ Черновик ответа готов",
            description: "Текст скопирован в буфер обмена",
          });
          
          navigator.clipboard.writeText(reply.reply_text).catch(() => {});
          break;

        case 'calculate_price':
          // Price calculation is handled via results, just show toast
          if (results?.price_calculation && !results.price_calculation.error) {
            setPriceCalculation(results.price_calculation);
            toast({
              title: "💰 Расчёт готов",
              description: `${results.price_calculation.category_name}: ${results.price_calculation.total_price.toLocaleString('ru-RU')} ₽`,
            });
          }
          break;

        case 'send_quote_whatsapp':
          if (results?.whatsapp_quote) {
            setWhatsappQuote(results.whatsapp_quote);
            toast({
              title: "📲 КП готово к отправке",
              description: "Нажмите кнопку для открытия WhatsApp",
            });
          }
          break;

        case 'get_quick_stats':
        case 'get_finance_summary':
          // Data queries - results are in the response
          break;

        default:
          console.warn(`Unknown action: ${action}`);
      }
    }
    
    // Also check results for price_calculation if action wasn't explicitly called
    if (results?.price_calculation && !results.price_calculation.error) {
      setPriceCalculation(results.price_calculation);
    }
    if (results?.whatsapp_quote) {
      setWhatsappQuote(results.whatsapp_quote);
    }
  }, [navigate, toast]);

  // Speak the response with appropriate TTS
  const speakResponse = useCallback(async (text: string) => {
    const settings = getVoiceSettings();
    
    if (settings.mode === 'text') {
      toast({
        title: "🤖 Ассистент",
        description: text,
      });
      return;
    }

    if (settings.mode === 'elevenlabs_tts' || settings.mode === 'elevenlabs_realtime') {
      try {
        await elevenLabsTTS.speak(text);
        return;
      } catch (err) {
        console.warn('[Voice] ElevenLabs failed, falling back to browser TTS:', err);
      }
    }

    if (browserTTS.isSupported) {
      browserTTS.speak(text);
    } else {
      toast({
        title: "🤖 Ассистент",
        description: text,
      });
    }
  }, [browserTTS, elevenLabsTTS, toast, getVoiceSettings]);

  // Main function to process voice command or pasted text
  const processCommand = useCallback(async (command: string): Promise<VoiceCommandResult> => {
    if (!command.trim()) {
      return { success: false, actions: [], speak: 'Пустая команда', error: 'Empty command' };
    }

    setIsProcessing(true);
    setParsedRequest(null);
    setDraftReply(null);
    setPriceCalculation(null);
    setWhatsappQuote(null);
    
    const isLongText = command.length > 200;
    console.log(`🎤 Processing ${isLongText ? 'pasted text' : 'voice command'}: "${command.substring(0, 100)}..."`);

    try {
      const { data, error } = await supabase.functions.invoke('voice-command', {
        body: { command, hotel_id: hotelId }
      });

      if (error) {
        console.error('Voice command error:', error);
        const result: VoiceCommandResult = {
          success: false,
          actions: [],
          speak: 'Ошибка обработки. Попробуйте ещё раз.',
          error: error.message
        };
        setLastResult(result);
        await speakResponse(result.speak);
        return result;
      }

      const result = data as VoiceCommandResult;
      setLastResult(result);

      // Execute actions with results context
      if (result.actions && result.actions.length > 0) {
        executeActions(result.actions, result.results);
      } else if (result.results) {
        // Handle results even without explicit actions
        if (result.results.price_calculation && !result.results.price_calculation.error) {
          setPriceCalculation(result.results.price_calculation);
        }
        if (result.results.whatsapp_quote) {
          setWhatsappQuote(result.results.whatsapp_quote);
        }
      }

      if (result.speak) {
        await speakResponse(result.speak);
      }

      return result;

    } catch (err) {
      console.error('Voice command exception:', err);
      const result: VoiceCommandResult = {
        success: false,
        actions: [],
        speak: 'Произошла ошибка. Попробуйте позже.',
        error: err instanceof Error ? err.message : 'Unknown error'
      };
      setLastResult(result);
      await speakResponse(result.speak);
      return result;

    } finally {
      setIsProcessing(false);
    }
  }, [hotelId, executeActions, speakResponse]);

  // Stop any ongoing TTS
  const stopSpeaking = useCallback(() => {
    browserTTS.stop();
    elevenLabsTTS.stop();
  }, [browserTTS, elevenLabsTTS]);

  // Create booking from parsed request
  const createBookingFromParsed = useCallback(() => {
    if (!parsedRequest) return;
    
    const params = new URLSearchParams();
    if (parsedRequest.guest_name) params.set('guest', parsedRequest.guest_name);
    if (parsedRequest.check_in) params.set('from', parsedRequest.check_in);
    if (parsedRequest.check_out) params.set('to', parsedRequest.check_out);
    if (parsedRequest.guests_count) params.set('guests', String(parsedRequest.guests_count));
    
    navigate(`/admin/room-grid?${params.toString()}`);
  }, [parsedRequest, navigate]);

  // Open QuickBookingModal with price calculation data
  const openQuickBookingWithPrice = useCallback(() => {
    if (!priceCalculation) return;
    
    // Dispatch custom event for QuickBookingModal to listen
    window.dispatchEvent(new CustomEvent('open-quick-booking', {
      detail: {
        category: priceCalculation.category_name,
        categoryId: priceCalculation.category_id,
        ratePlanId: priceCalculation.rate_plan_id,
        checkIn: priceCalculation.check_in,
        checkOut: priceCalculation.check_out,
        adults: priceCalculation.adults,
        children: priceCalculation.children_count,
        totalAmount: priceCalculation.total_price,
        // Also include parsed request data if available
        guestName: parsedRequest?.guest_name,
        guestPhone: parsedRequest?.phone,
        guestEmail: parsedRequest?.email,
      }
    }));
    
    toast({
      title: "📅 Открываю форму бронирования",
      description: `${priceCalculation.category_name}: ${priceCalculation.check_in} — ${priceCalculation.check_out}`,
    });
  }, [priceCalculation, parsedRequest, toast]);

  // Open WhatsApp with prepared quote
  const openWhatsAppQuote = useCallback(() => {
    if (!whatsappQuote?.whatsapp_url) return;
    
    window.open(whatsappQuote.whatsapp_url, '_blank');
    
    toast({
      title: "📲 WhatsApp открыт",
      description: "Отправьте сообщение гостю",
    });
  }, [whatsappQuote, toast]);

  // Clear results
  const clearResults = useCallback(() => {
    setParsedRequest(null);
    setDraftReply(null);
    setPriceCalculation(null);
    setWhatsappQuote(null);
    setLastResult(null);
  }, []);

  return {
    processCommand,
    stopSpeaking,
    createBookingFromParsed,
    openQuickBookingWithPrice,
    openWhatsAppQuote,
    clearResults,
    isProcessing,
    lastResult,
    parsedRequest,
    draftReply,
    priceCalculation,
    whatsappQuote,
    isSpeaking: browserTTS.isPlaying || elevenLabsTTS.isPlaying,
    isLoadingTTS: elevenLabsTTS.isLoading,
  };
};
